'use client'

import {
  createContext,
  useContext,
  useEffect,
  useRef,
  type CSSProperties,
} from 'react'
import { cx } from 'class-variance-authority'
import {
  DayButton,
  Dropdown as RdpDropdown,
  DayPicker,
  getDefaultClassNames,
  UI,
} from 'react-day-picker'
import { format } from 'date-fns'
import { fr, nl, enUS } from 'date-fns/locale'
import { CaretDownIcon } from '@phosphor-icons/react/dist/ssr/CaretDown'
import { CaretLeftIcon } from '@phosphor-icons/react/dist/ssr/CaretLeft'
import { CaretRightIcon } from '@phosphor-icons/react/dist/ssr/CaretRight'
import { SupportedLanguages } from '@skop-app/lib-domain/types'

import { DropdownMenu } from '../DropdownMenu/dropdownMenu.atom'

/** Accent palette for calendar range, day cells, and caption dropdowns (CSS variables). */
export interface CalendarAccentColors {
  primary: string
  primary25: string
  primary50: string
  primary75: string
  onPrimary: string
  onPrimary25: string
}

const CalendarAccentContext = createContext<CalendarAccentColors | null>(null)

const calendarAccentCssVars = (accent: CalendarAccentColors): CSSProperties => {
  return {
    '--skop-cal-primary': accent.primary,
    '--skop-cal-primary25': accent.primary25,
    '--skop-cal-primary50': accent.primary50,
    '--skop-cal-primary75': accent.primary75,
    '--skop-cal-on-primary': accent.onPrimary,
    '--skop-cal-on-primary25': accent.onPrimary25,
  } as CSSProperties
}

type CaptionDropdownProps = React.ComponentProps<typeof RdpDropdown>

const CalendarCaptionDropdownMenu = (props: CaptionDropdownProps) => {
  const accent = useContext(CalendarAccentContext)
  const {
    options = [],
    className,
    classNames,
    disabled,
    onChange,
    value,
    'aria-label': ariaLabel,
  } = props

  const rawValue = Array.isArray(value) ? value[0] : value
  const numericValue =
    typeof rawValue === 'string'
      ? Number.parseInt(rawValue, 10)
      : Number(rawValue)
  const selected = options.find((o) => o.value === numericValue)

  function emitChange(next: number) {
    if (disabled) return
    onChange?.({
      target: { value: String(next) },
    } as React.ChangeEvent<HTMLSelectElement>)
  }

  return (
    <span
      data-disabled={disabled ? true : undefined}
      className={cx(classNames[UI.DropdownRoot])}
    >
      <DropdownMenu.Root>
        <DropdownMenu.Trigger
          type='button'
          disabled={disabled}
          aria-label={ariaLabel}
          className={cx(
            'inline-flex h-9 max-w-full min-w-0 items-center gap-1 rounded-lg border border-zinc-200 bg-white px-2.5 py-1 text-sm font-medium text-slate-900 shadow-sm transition-colors hover:bg-zinc-50 focus:outline-none focus-visible:ring-2 disabled:cursor-not-allowed disabled:opacity-50',
            accent
              ? 'focus-visible:ring-[var(--skop-cal-primary)] focus-visible:ring-offset-0'
              : 'focus-visible:ring-primary-400/40',
            classNames[UI.Dropdown],
            className,
          )}
        >
          <span className='min-w-0 flex-1 truncate text-left'>
            {selected?.label ?? '—'}
          </span>
          <CaretDownIcon className='size-4 shrink-0 text-gray-500' />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content
          align='start'
          sideOffset={4}
          className='z-[200] max-h-60 min-w-[var(--radix-dropdown-menu-trigger-width)] overflow-y-auto p-0'
          style={accent ? calendarAccentCssVars(accent) : undefined}
        >
          {options.map((opt) => {
            const isCurrent = opt.value === numericValue
            return (
              <DropdownMenu.Item
                key={opt.value}
                disabled={opt.disabled}
                className={cx(
                  accent
                    ? cx(
                        'cursor-pointer !text-slate-800 [&:hover]:!bg-[var(--skop-cal-primary25)] [&:hover]:!text-[var(--skop-cal-on-primary25)] [&:focus]:!bg-[var(--skop-cal-primary25)] [&:focus]:!text-[var(--skop-cal-on-primary25)]',
                        isCurrent &&
                          '!bg-[var(--skop-cal-primary)] !text-[var(--skop-cal-on-primary)] [&:hover]:!bg-[var(--skop-cal-primary)] [&:hover]:!text-[var(--skop-cal-on-primary)] [&:focus]:!bg-[var(--skop-cal-primary)] [&:focus]:!text-[var(--skop-cal-on-primary)]',
                      )
                    : cx('cursor-pointer', isCurrent && 'bg-primary-100'),
                )}
                onSelect={(e) => {
                  e.preventDefault()
                  emitChange(opt.value)
                }}
              >
                {opt.label}
              </DropdownMenu.Item>
            )
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Root>
    </span>
  )
}

const CalendarRoot = ({
  className,
  classNames,
  showOutsideDays = true,
  captionLayout = 'dropdown',
  formatters,
  components,
  isDisabled = false,
  language = 'fr',
  accentColors,
  ...props
}: React.ComponentProps<typeof DayPicker> & {
  language?: SupportedLanguages
  isDisabled?: boolean
  accentColors?: CalendarAccentColors
}) => {
  const defaultClassNames = getDefaultClassNames()
  const accent = accentColors

  const getLocale = () => {
    if (language === 'fr') return fr
    if (language === 'en') return enUS
    return nl
  }

  return (
    <CalendarAccentContext.Provider value={accent ?? null}>
      <DayPicker
        locale={getLocale()}
        showOutsideDays={showOutsideDays}
        disabled={isDisabled}
        className={cx(
          'bg-white group/calendar py-4 px-6 rounded-xl shadow-md border border-gray-200 [--cell-size:2rem]',
          className,
        )}
        captionLayout={captionLayout}
        formatters={{
          formatMonthDropdown: (date) =>
            format(date, 'MMM', { locale: getLocale() }),
          ...formatters,
        }}
        classNames={{
          root: cx('w-fit', defaultClassNames.root),
          months: cx(
            'flex gap-4 flex-col md:flex-row relative',
            defaultClassNames.months,
          ),
          month: cx('flex flex-col w-full gap-6', defaultClassNames.month),
          nav: cx(
            'flex items-center gap-3 w-full absolute top-0 inset-x-0 justify-between',
            defaultClassNames.nav,
          ),
          button_previous: cx(
            'h-10 w-10 bg-white border-0 hover:bg-gray-100 rounded-lg transition-colors duration-150 aria-disabled:opacity-50 p-0 select-none flex items-center justify-center',
            defaultClassNames.button_previous,
          ),
          button_next: cx(
            'h-10 w-10 bg-white border-0 hover:bg-gray-100 rounded-lg transition-colors duration-150 aria-disabled:opacity-50 p-0 select-none flex items-center justify-center',
            defaultClassNames.button_next,
          ),
          month_caption: cx(
            'flex items-center justify-center h-10 w-full px-12 mb-2 min-w-0',
            defaultClassNames.month_caption,
          ),
          dropdowns: cx(
            'w-full flex items-center text-base font-medium justify-center h-10 gap-3',
            defaultClassNames.dropdowns,
          ),
          dropdown_root: cx(
            'relative border border-gray-300 rounded-lg bg-white hover:bg-gray-50 transition-colors',
            defaultClassNames.dropdown_root,
          ),
          dropdown: cx(defaultClassNames.dropdown),
          caption_label: cx(
            'select-none font-medium text-slate-900 truncate',
            captionLayout === 'label'
              ? 'text-base'
              : 'rounded-lg px-4 py-2 flex items-center gap-2 text-base bg-white border border-gray-300 [&>svg]:text-gray-500 [&>svg]:size-4',
            defaultClassNames.caption_label,
          ),
          table: 'w-full border-collapse',
          weekdays: cx('flex mb-3', defaultClassNames.weekdays),
          weekday: cx(
            'text-gray-500 rounded-md flex-1 font-normal text-sm capitalize tracking-normal select-none',
            defaultClassNames.weekday,
          ),
          week: cx('flex w-full mt-2', defaultClassNames.week),
          week_number_header: cx(
            'select-none w-10',
            defaultClassNames.week_number_header,
          ),
          week_number: cx(
            'text-xs select-none text-slate-500 font-medium',
            defaultClassNames.week_number,
          ),
          day: cx(
            'relative w-full h-full p-1 text-center group/day aspect-square select-none min-h-[--cell-size] min-w-[--cell-size]',
            defaultClassNames.day,
          ),
          range_start: cx(
            accent
              ? 'rounded-l-xl rounded-r-none bg-[var(--skop-cal-primary)] text-[var(--skop-cal-on-primary)]'
              : 'rounded-l-xl bg-primary-400 text-white rounded-r-none',
            defaultClassNames.range_start,
          ),
          range_middle: cx(
            accent
              ? 'rounded-none bg-[var(--skop-cal-primary25)] text-[var(--skop-cal-on-primary25)]'
              : 'rounded-none bg-primary-100 text-slate-800',
            defaultClassNames.range_middle,
          ),
          range_end: cx(
            accent
              ? 'rounded-r-xl rounded-l-none bg-[var(--skop-cal-primary)] text-[var(--skop-cal-on-primary)]'
              : 'rounded-r-xl bg-primary-400 text-white rounded-l-none',
            defaultClassNames.range_end,
          ),
          today: cx(
            accent
              ? 'text-[color-mix(in_srgb,var(--skop-cal-primary)_85%,#000)] underline underline-offset-4 data-[range-middle=true]:bg-[var(--skop-cal-primary25)] data-[range-start=true]:bg-[var(--skop-cal-primary)] data-[range-end=true]:bg-[var(--skop-cal-primary)] data-[range-middle=true]:text-[var(--skop-cal-on-primary25)] data-[range-start=true]:text-[var(--skop-cal-on-primary)] data-[range-end=true]:text-[var(--skop-cal-on-primary)]'
              : 'text-primary-800 underline underline-offset-4 data-[range-middle=true]:bg-primary-100 data-[range-start=true]:bg-primary-400 data-[range-end=true]:bg-primary-400 data-[range-middle=true]:text-slate-800',
            'data-[range-start=false]:data-[range-end=false]:data-[range-middle=false]:rounded-xl',
            defaultClassNames.today,
          ),
          outside: cx(
            'text-gray-400 aria-selected:text-gray-400',
            defaultClassNames.outside,
          ),
          disabled: cx('text-gray-300 opacity-60', defaultClassNames.disabled),
          hidden: cx('invisible', defaultClassNames.hidden),
          ...classNames,
        }}
        components={{
          Root: ({ className, rootRef, style, ...rootProps }) => {
            const rootAccent = useContext(CalendarAccentContext)
            return (
              <div
                data-slot='calendar'
                ref={rootRef}
                className={cx(className)}
                style={{
                  ...(rootAccent ? calendarAccentCssVars(rootAccent) : {}),
                  ...style,
                }}
                {...rootProps}
              />
            )
          },
          Chevron: ({ className, orientation, ...props }) => {
            if (orientation === 'left') {
              return (
                <CaretLeftIcon className={cx('size-5', className)} {...props} />
              )
            }

            if (orientation === 'right') {
              return (
                <CaretRightIcon
                  className={cx('size-5', className)}
                  {...props}
                />
              )
            }

            return (
              <CaretDownIcon className={cx('size-5', className)} {...props} />
            )
          },
          DayButton: CalendarDayButton,
          Dropdown: CalendarCaptionDropdownMenu,
          WeekNumber: ({ children, ...props }) => {
            return (
              <td {...props}>
                <div className='flex size-[var(--cell-size)] items-center justify-center text-center'>
                  {children}
                </div>
              </td>
            )
          },
          ...components,
        }}
        {...props}
      />
    </CalendarAccentContext.Provider>
  )
}

const CalendarDayButton = ({
  className,
  day,
  modifiers,
  ...props
}: React.ComponentProps<typeof DayButton>) => {
  const accent = useContext(CalendarAccentContext)
  const defaultClassNames = getDefaultClassNames()

  const ref = useRef<HTMLButtonElement>(null)
  useEffect(() => {
    if (modifiers.focused) ref.current?.focus()
  }, [modifiers.focused])

  return (
    <button
      ref={ref}
      type='button'
      data-day={day.date.toLocaleDateString()}
      data-selected-single={
        modifiers.selected &&
        !modifiers.range_start &&
        !modifiers.range_end &&
        !modifiers.range_middle
      }
      data-range-start={modifiers.range_start}
      data-range-end={modifiers.range_end}
      data-range-middle={modifiers.range_middle}
      className={cx(
        'flex items-center justify-center w-full h-full rounded-xl font-normal text-sm transition-colors duration-150',
        accent
          ? 'hover:bg-[var(--skop-cal-primary25)] hover:text-[var(--skop-cal-on-primary25)]'
          : 'hover:bg-primary-100',
        'focus:outline-none',
        'disabled:opacity-30 disabled:cursor-not-allowed disabled:hover:bg-transparent',
        accent
          ? cx(
              'data-[selected-single=true]:bg-[var(--skop-cal-primary)] data-[selected-single=true]:text-[var(--skop-cal-on-primary)] data-[selected-single=true]:font-medium data-[selected-single=true]:hover:bg-[var(--skop-cal-primary)]',
              'data-[range-start=true]:bg-[var(--skop-cal-primary)] data-[range-start=true]:text-[var(--skop-cal-on-primary)] data-[range-start=true]:font-medium data-[range-start=true]:rounded-l-xl data-[range-start=true]:rounded-r-none data-[range-start=true]:hover:bg-[var(--skop-cal-primary)]',
              'data-[range-end=true]:bg-[var(--skop-cal-primary)] data-[range-end=true]:text-[var(--skop-cal-on-primary)] data-[range-end=true]:font-medium data-[range-end=true]:rounded-r-xl data-[range-end=true]:rounded-l-none data-[range-end=true]:hover:bg-[var(--skop-cal-primary)]',
              'data-[range-middle=true]:bg-[var(--skop-cal-primary25)] data-[range-middle=true]:text-[var(--skop-cal-on-primary25)] data-[range-middle=true]:rounded-none data-[range-middle=true]:font-medium data-[range-middle=true]:hover:bg-[var(--skop-cal-primary25)] data-[range-middle=true]:hover:text-[var(--skop-cal-on-primary25)]',
            )
          : cx(
              'data-[selected-single=true]:bg-primary-500 data-[selected-single=true]:text-slate-800 data-[selected-single=true]:font-medium data-[selected-single=true]:hover:bg-primary-500',
              'data-[range-start=true]:bg-primary-500 data-[range-start=true]:text-white data-[range-start=true]:font-medium data-[range-start=true]:rounded-l-xl data-[range-start=true]:rounded-r-none data-[range-start=true]:hover:bg-primary-500',
              'data-[range-end=true]:bg-primary-500 data-[range-end=true]:text-white data-[range-end=true]:font-medium data-[range-end=true]:rounded-r-xl data-[range-end=true]:rounded-l-none data-[range-end=true]:hover:bg-primary-500',
              'data-[range-middle=true]:bg-primary-100 data-[range-middle=true]:text-slate-800 data-[range-middle=true]:rounded-none data-[range-middle=true]:font-medium data-[range-middle=true]:hover:bg-primary-100',
            ),
        defaultClassNames.day,
        className,
      )}
      {...props}
    />
  )
}

export const Calendar: {
  Root: typeof CalendarRoot
  DayButton: typeof CalendarDayButton
} = {
  Root: CalendarRoot,
  DayButton: CalendarDayButton,
}
